import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import ProgramLogo from "./ProgramLogo";

/**
 * Masonry hub layout: featured row (large cards) + supporting row (compact cards).
 *
 * Programs are split by their `is_featured` boolean (admin-controlled). If no
 * program is marked featured we fall back to the first 3 by sort_order so the
 * page never breaks. Sort_order is honoured WITHIN each row.
 */
export default function ProgramMasonryGrid({ programs = [] }) {
  const sorted = [...programs].sort(
    (a, b) =>
      (a.sort_order ?? a.display_order ?? 100) -
      (b.sort_order ?? b.display_order ?? 100)
  );
  let featured = sorted.filter((p) => p.is_featured);
  let supporting = sorted.filter((p) => !p.is_featured);
  if (featured.length === 0 && sorted.length > 0) {
    featured = sorted.slice(0, Math.min(3, sorted.length));
    supporting = sorted.slice(featured.length);
  }
  // Cap featured at 3 to keep the row uniform; overflow drops to supporting.
  if (featured.length > 3) {
    supporting = [...featured.slice(3), ...supporting];
    featured = featured.slice(0, 3);
  }

  return (
    <div className="grid gap-8" data-testid="program-masonry-grid">
      {featured.length > 0 && (
        <div
          className="grid gap-6 lg:grid-cols-3 sm:grid-cols-2 grid-cols-1"
          data-testid="program-masonry-featured-row"
        >
          {featured.map((p) => (
            <FeaturedProgramCard key={p.slug} program={p} />
          ))}
        </div>
      )}
      {supporting.length > 0 && (
        <div
          className="grid gap-5 lg:grid-cols-4 md:grid-cols-2 grid-cols-1"
          data-testid="program-masonry-supporting-row"
        >
          {supporting.map((p) => (
            <SupportingProgramCard key={p.slug} program={p} />
          ))}
        </div>
      )}
    </div>
  );
}

// -- Featured card (Row 1, large) -------------------------------------------
function FeaturedProgramCard({ program }) {
  const accent = program.accent_color || "#0369A1";
  const secondary = program.secondary_color || "#E0F2FE";
  return (
    <article
      className="group relative flex flex-col overflow-hidden rounded-[2rem] border border-slate-200/70 bg-white hover:shadow-lg hover:border-slate-300 transition-all"
      data-testid={`program-masonry-card-${program.slug}`}
      data-row="featured"
    >
      {/* Top coloured banner */}
      <div
        className="relative h-40 sm:h-44 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${secondary} 0%, #FFFFFF 95%)`,
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <ProgramLogo program={program} size={96} rounded="rounded-3xl" />
        </div>
        {program.brand_label && (
          <span
            className="absolute bottom-3 left-4 pill text-[10px]"
            style={{ backgroundColor: "#FFFFFFCC", color: accent }}
          >
            {program.brand_label}
          </span>
        )}
        {/* Top accent stripe */}
        <span
          className="absolute top-0 left-0 right-0 h-1.5"
          style={{ backgroundColor: accent }}
        />
      </div>

      <div className="flex flex-1 flex-col p-7">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className="pill text-[10px]"
            style={{ backgroundColor: secondary, color: accent }}
          >
            {program.category || "Program"}
          </span>
          {program.age_range && (
            <span className="pill bg-slate-100 text-slate-600 text-[10px]">
              {program.age_range}
            </span>
          )}
          {program.class_length && (
            <span className="pill bg-slate-100 text-slate-600 text-[10px]">
              {program.class_length}
            </span>
          )}
        </div>
        <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-slate-900 leading-snug">
          {program.name}
        </h3>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
          {program.short_description || program.tagline || program.description}
        </p>
        <div className="mt-6 flex flex-wrap gap-2.5">
          <Link
            to={`/programs/${program.slug}`}
            className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-xs font-bold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: accent }}
            data-testid={`program-masonry-cta-learn-${program.slug}`}
          >
            Learn more
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
          {program.bookwhen_url && (
            <a
              href={program.bookwhen_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border px-5 py-2.5 text-xs font-bold transition-colors hover:bg-slate-50"
              style={{ borderColor: accent, color: accent }}
              data-testid={`program-masonry-cta-schedule-${program.slug}`}
            >
              View schedule
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

// -- Supporting card (Row 2, compact) ---------------------------------------
function SupportingProgramCard({ program }) {
  const accent = program.accent_color || "#0369A1";
  const secondary = program.secondary_color || "#E0F2FE";
  return (
    <article
      className="group relative flex flex-col overflow-hidden rounded-[1.5rem] border border-slate-200/70 bg-white hover:shadow-md hover:border-slate-300 transition-all"
      data-testid={`program-masonry-card-${program.slug}`}
      data-row="supporting"
    >
      <span
        className="absolute top-0 left-0 right-0 h-1"
        style={{ backgroundColor: accent }}
      />
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start gap-3">
          <ProgramLogo program={program} size={44} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className="pill text-[10px]"
                style={{ backgroundColor: secondary, color: accent }}
              >
                {program.category || "Program"}
              </span>
            </div>
            <h3 className="mt-1.5 font-display text-base font-extrabold tracking-tight text-slate-900 leading-snug">
              {program.name}
            </h3>
            {program.age_range && (
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mt-1">
                {program.age_range}
              </p>
            )}
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-600 leading-relaxed line-clamp-3 flex-1">
          {program.short_description || program.tagline}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Link
            to={`/programs/${program.slug}`}
            className="inline-flex items-center gap-1 text-xs font-bold transition-opacity hover:opacity-80"
            style={{ color: accent }}
            data-testid={`program-masonry-cta-learn-${program.slug}`}
          >
            Learn more
            <ArrowUpRight className="h-3 w-3" />
          </Link>
          {program.bookwhen_url && (
            <a
              href={program.bookwhen_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700"
              data-testid={`program-masonry-cta-schedule-${program.slug}`}
            >
              View schedule
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

export { FeaturedProgramCard, SupportingProgramCard };
